import startRouteInvalidation from "@mittwald/flow-app-utils/dist/api/startRouteInvalidation";
import { apiOperationResourceStore } from "@mittwald/flow-lib/dist/resources/ApiOperationResource";
import { z } from "zod";
import { ingressListIngressesCompatibleWithCertificate } from "../api/Mittwald";
import websocketGateway from "../api/websocketGateway";
import { invalidatePath } from "../api_NEXT/clients/cache";
import { lastEventIdStore } from "../api_NEXT/lib/lastEventIdStore";
import type { CleanupFn } from "../lib/autorunWithCleanup";
import autorunWithCleanup from "../lib/autorunWithCleanup";
import { TokenRefresh } from "../model/signup/TokenRefresh";
import User from "../model/user/User";
import { refreshProvideReactCache } from "../models_NEXT";
import { clearProvideReactCache } from "../models_NEXT/react/asyncResourceInvalidation";
import sessionStore from "../store/session";

const resetApiResourceStore = (): void => {
  apiOperationResourceStore
    .findByTag("**")
    .forEach((r) => r.expire({ autoReload: false }));
  apiOperationResourceStore.clear();
};

export const autorunAppInitialization = (): void => {
  autorunWithCleanup(() => {
    void TokenRefresh.startAutoRefresh();
  });

  autorunWithCleanup(() => {
    const accessToken = sessionStore.accessToken;

    const cleanupFunctions: CleanupFn[] = [];

    if (accessToken) {
      resetApiResourceStore();
      clearProvideReactCache();

      void websocketGateway.connect({
        uri: import.meta.env.VITE_APP_CENTRIFUGE_URI,
        accessToken,
      });

      cleanupFunctions.push(() => websocketGateway.disconnect());

      void User.me().then((user) => {
        // NEXT models / client
        cleanupFunctions.push(
          websocketGateway.subscribe({
            userId: user.id,
            channel: "cache_invalidate",
            dataSchema: z.object({
              url: z.string(),
              // if-event-reached
              eventId: z.string(),
            }),
            handler: async ({ url, eventId }) => {
              lastEventIdStore.set(url, eventId);
              const urlWithoutLeadingSlash = url.startsWith("/")
                ? url.slice(1)
                : url;
              // first invalidate request cache
              await invalidatePath(urlWithoutLeadingSlash);
              // second invalidate React cache
              refreshProvideReactCache(urlWithoutLeadingSlash);
            },
          }),
        );

        // Legacy client cache
        cleanupFunctions.push(
          startRouteInvalidation(websocketGateway, user.id, (res) => {
            return (
              res.tags.matches("GET") ||
              res.tags.matches(
                ingressListIngressesCompatibleWithCertificate.operationId,
              )
            );
          }),
        );
      });
    }

    return cleanupFunctions;
  });
};
